import React, { useCallback, useEffect, useState } from 'react';

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $isHeadingNode } from '@lexical/rich-text';
import {
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_LOW,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';
import { mergeRegister } from '@lexical/utils';
import { $getSelectionStyleValueForProperty } from '@lexical/selection';
import { useTheme } from '@mui/material';

import { Box } from '../../../../Box';
import { DropdownButton } from '../../../../DropdownButton';
import { Typography } from '../../../../Typography';
import { Icon } from '../../../../Icon';
import { ToolbarButton } from '../common';

import {
  useTagsItems,
  BlockTypeKeys,
  blockTypeToBlockName,
  useColorsItems,
  useBgColorsItems,
} from './hooks';

interface FormatHeadingPluginProps {
  heading: boolean;
  fontColor: boolean;
  backgroungColor: boolean;
}

export const FormatHeadingPlugin = ({
  heading,
  fontColor,
  backgroungColor,
}: FormatHeadingPluginProps) => {
  const theme = useTheme();
  const [editor] = useLexicalComposerContext();

  const [selectedBlockType, setSelectedBlockType] =
    useState<BlockTypeKeys>('paragraph');
  const [selectedColorType, setSelectedColorType] = useState(
    theme.tokens.colors.text.light.primary,
  );
  const [selectedBgColorType, setSelectedBgColorType] = useState(
    theme.tokens.colors.bg.light.primary,
  );
  const [isBgColorApplyed, setIsBgColorApplyed] = useState(false);

  const tags = useTagsItems(editor, selectedBlockType);
  const colors = useColorsItems(editor, selectedColorType);
  const bgColors = useBgColorsItems(editor, selectedBgColorType);

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();

    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode();
      const element =
        anchorNode.getKey() === 'root'
          ? anchorNode
          : anchorNode.getTopLevelElementOrThrow();
      const elementKey = element.getKey();
      const elementDOM = editor.getElementByKey(elementKey);

      if (elementDOM !== null) {
        const type = $isHeadingNode(element)
          ? element.getTag()
          : element.getType();

        if (type in blockTypeToBlockName) {
          setSelectedBlockType(type as BlockTypeKeys);
        }
      }

      const currentColor = $getSelectionStyleValueForProperty(
        selection,
        'color',
        theme.tokens.colors.text.light.primary,
      );

      if (currentColor) {
        setSelectedColorType(currentColor);
      }

      const defaultBgColor = theme.tokens.colors.bg.light.primary;
      const currentBgColor = $getSelectionStyleValueForProperty(
        selection,
        'background-color',
        defaultBgColor,
      );

      if (currentBgColor) {
        setIsBgColorApplyed(() => currentBgColor !== defaultBgColor);
        setSelectedBgColorType(currentBgColor);
      }
    }
  }, [
    editor,
    theme.tokens.colors.bg.light.primary,
    theme.tokens.colors.text.light.primary,
  ]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateToolbar();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          updateToolbar();
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
    );
  }, [editor, updateToolbar]);

  return (
    <Box display="flex" alignItems="center" gap="2">
      {heading && (
        <DropdownButton
          menuItems={tags}
          variant="tertiaryLight"
          verticalPosition="bottom"
          horizontalPosition="left"
          button={({ onClick }) => (
            <ToolbarButton onClick={onClick} tooltip="Стили текста">
              <Box display="flex" alignItems="center" gap="4">
                <Typography variant="caption">
                  {blockTypeToBlockName[selectedBlockType]}
                </Typography>
                <Icon name="dropdown" size="16" color="text.light.primary" />
              </Box>
            </ToolbarButton>
          )}
        />
      )}

      {fontColor && (
        <DropdownButton
          menuItems={colors}
          variant="tertiaryLight"
          verticalPosition="bottom"
          horizontalPosition="left"
          button={({ onClick }) => (
            <ToolbarButton onClick={onClick} tooltip="Цвет текста">
              <Box display="flex" alignItems="center" gap="4">
                <Box
                  width="16px"
                  height="16px"
                  radius="xs"
                  style={{ backgroundColor: selectedColorType }}
                />
                <Icon name="dropdown" size="16" color="text.light.primary" />
              </Box>
            </ToolbarButton>
          )}
        />
      )}

      {backgroungColor && (
        <DropdownButton
          menuItems={bgColors}
          variant="tertiaryLight"
          verticalPosition="bottom"
          horizontalPosition="left"
          button={({ onClick }) => (
            <ToolbarButton onClick={onClick} tooltip="Фон текста">
              <Box display="flex" alignItems="center" gap="4">
                <Box display="flex" alignItems="center" justifyContent="center">
                  <Icon
                    name="marker_outline"
                    size="16"
                    color={
                      isBgColorApplyed
                        ? 'original.brand.primary'
                        : 'text.light.primary'
                    }
                  />
                  <Box style={{ backgroundColor: selectedBgColorType }} />
                </Box>

                <Icon name="dropdown" size="16" color="text.light.primary" />
              </Box>
            </ToolbarButton>
          )}
        />
      )}
    </Box>
  );
};
